/**
 * Identity Data Layer Normalization Utilities
 *
 * Normalizes and hashes user identity data according to global specification
 * for GB, US, AU, NZ markets.
 *
 * All normalization happens BEFORE hashing to ensure consistency across platforms.
 */

/**
 * SHA256 hashing utility
 * @param value - The normalized string to hash
 * @returns Lowercase hex string of SHA256 hash
 */
export async function sha256Hash(value: string): Promise<string> {
  if (!value) {
    return '';
  }

  // Use Web Crypto API (available in modern browsers and Node 20+)
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  return '';
}

/**
 * Normalize email address
 * - Trim whitespace
 * - Lowercase
 * - Keep + symbol (no content changes)
 *
 * @param email - Raw email address
 * @returns Normalized email
 */
export function normalizeEmail(email: string | undefined | null): string {
  if (!email) {
    return '';
  }
  return email.trim().toLowerCase();
}

/**
 * Normalize phone number based on country
 * - Strip all non-digits
 * - Apply country-specific formatting
 * - Return digits-only string with country code (E.164-like without +)
 *
 * @param phone - Raw phone number
 * @param country - ISO-2 country code ('gb', 'us', 'au', 'nz')
 * @returns Normalized phone number with country code
 */
export function normalizePhone(phone: string | undefined | null, country: string): string {
  if (!phone) {
    return '';
  }

  // Strip all non-digits
  const digitsOnly = phone.replace(/\D/g, ''); //NOSONAR - replaceAll not available in ES6 target
  if (!digitsOnly) {
    return '';
  }

  // Country code lookup table
  const countryCodeMap: Record<string, { code: string; prefixes: string[] }> = {
    gb: { code: '44', prefixes: ['44', '0'] },
    us: { code: '1', prefixes: ['1'] },
    au: { code: '61', prefixes: ['61', '0'] },
    nz: { code: '64', prefixes: ['64', '0'] },
  };

  const countryLower = country.toLowerCase();
  const countryData = countryCodeMap[countryLower];

  if (!countryData) {
    return digitsOnly;
  }

  // For US: handle special case where "1" prefix could be country code or part of number
  if (countryLower === 'us') {
    // Exactly 11 digits starting with 1 = already has country code
    if (digitsOnly.startsWith('1') && digitsOnly.length === 11) {
      return digitsOnly;
    }
    // 10 digits = add country code
    if (digitsOnly.length === 10) {
      return `${countryData.code}${digitsOnly}`;
    }
    // Any other length = add country code
    return `${countryData.code}${digitsOnly}`;
  }

  // For other countries: if already has country code, return as-is
  if (digitsOnly.startsWith(countryData.code)) {
    return digitsOnly;
  }

  // Strip leading 0 if present and add country code
  if (digitsOnly.startsWith('0')) {
    return `${countryData.code}${digitsOnly.substring(1)}`;
  }

  return `${countryData.code}${digitsOnly}`;
}

/**
 * Normalize date of birth
 * - Format as YYYY-MM-DD
 * - Pad month/day to 2 digits
 * - Parse YYYY-MM-DD strings deterministically
 *
 * @param dob - Raw date (string in YYYY-MM-DD format or Date object)
 * @returns Normalized DOB string (YYYY-MM-DD)
 */
export function normalizeDOB(dob: string | Date | undefined | null): string {
  if (!dob) {
    return '';
  }

  if (typeof dob === 'string') {
    // Parse YYYY-MM-DD or YYYY-M-D format manually
    const regex = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
    const match = regex.exec(dob.trim());
    if (match) {
      const [, year, month, day] = match;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return ''; // Invalid format
  }

  // Handle Date objects - use UTC accessors to prevent timezone shifts
  const year = dob.getUTCFullYear();
  const month = String(dob.getUTCMonth() + 1).padStart(2, '0');
  const day = String(dob.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Normalize country code to ISO-2 lowercase
 *
 * @param country - Raw country value
 * @returns ISO-2 country code lowercase
 */
export function normalizeCountry(country: string | undefined | null): string {
  if (!country) {
    return '';
  }

  const countryUpper = country.trim().toUpperCase();

  // Map common variations
  const countryMap: Record<string, string> = {
    GB: 'gb',
    UK: 'gb',
    'UNITED KINGDOM': 'gb',
    US: 'us',
    USA: 'us',
    'UNITED STATES': 'us',
    AU: 'au',
    AUS: 'au',
    AUSTRALIA: 'au',
    NZ: 'nz',
    'NEW ZEALAND': 'nz',
  };

  return countryMap[countryUpper] || country.trim().toLowerCase();
}
